// src/App.js

import React, { useState, useRef } from 'react';
import ProjectIntro from './ProjectIntro';
import RoomForm     from './RoomForm';
import { saveProjectToDisk, loadProjectFromFile } from './utils/saveProject';
import { generateReport } from './utils/generateReport';

export default function App() {
  const fileInputRef = useRef(null);

  const [projectInfo, setProjectInfo] = useState({
    assessor:      '',
    projectName:   '',
    projectType:   '',
    dateTime:      new Date().toISOString().slice(0, 10),
    propertyPhoto: null,
  });
  const [rooms, setRooms] = useState([]);

  // --- Load / Save handlers ---
  const openFilePicker = () => fileInputRef.current.click();
  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const loaded = await loadProjectFromFile(file);
      setProjectInfo(loaded.projectInfo);
      setRooms(loaded.rooms);
    } catch (err) {
      console.error(err);
      alert(`Failed to load project:\n${err.message}`);
    }
    e.target.value = '';
  };
  const doSave = (roomsToSave = rooms) =>
    saveProjectToDisk(projectInfo, roomsToSave);

  // --- Room CRUD ---
  const addRoom = () => {
    setRooms(rs => {
      const nextId = rs.length ? Math.max(...rs.map(r => r.id)) + 1 : 1;
      const newRoom = {
        id: nextId,
        room: '',
        area: '',
        floor: '',
        compassDirection: '',
        observations: '',
        observationsPhotos: [],
        labResults: [],
        iaqReadings: null,
        recommendations: '',
        requirements: { requirement: '', approxFt: '' },
      };
      const updated = [...rs, newRoom];
      doSave(updated);
      return updated;
    });
  };
  const updateRoom = (id, patch) =>
    setRooms(rs => {
      const updated = rs.map(r => (r.id === id ? { ...r, ...patch } : r));
      doSave(updated);
      return updated;
    });
  const deleteRoom = id =>
    setRooms(rs => {
      const updated = rs.filter(r => r.id !== id);
      doSave(updated);
      return updated;
    });

  // --- Generate Report ---
  const handleGenerate = async () => {
    if (!rooms.length) {
      alert('Add at least one room before generating report.');
      return;
    }
    doSave();
    try {
      await generateReport(projectInfo, rooms);
    } catch (err) {
      console.error(err);
      alert(`Failed to generate report:\n${err.message}`);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".gz"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* ← Buttons now immediately above ProjectIntro, left-aligned */}
      <div className="flex space-x-2 mb-4">
        <button
          className="bg-gray-600 text-white px-3 py-1 rounded"
          onClick={openFilePicker}
        >
          Load Project
        </button>
        <button
          className="bg-blue-600 text-white px-3 py-1 rounded"
          onClick={() => doSave()}
        >
          Save Project
        </button>
      </div>

      {/* Project Information panel */}
      <ProjectIntro
        data={projectInfo}
        onChange={(field, value) =>
          setProjectInfo(prev => ({ ...prev, [field]: value }))
        }
      />

      {/* One RoomForm per room */}
      {rooms.map(room => (
        <RoomForm
          key={room.id}
          roomData={room}
          onDataChange={updateRoom}
          onDeleteRoom={() => deleteRoom(room.id)}
        />
      ))}

      {/* Bottom-row actions */}
      <div className="mt-6 flex space-x-4">
        <button
          className="bg-green-600 text-white px-4 py-2 rounded"
          onClick={addRoom}
        >
          Add Room
        </button>
        <button
          className="bg-indigo-600 text-white px-4 py-2 rounded"
          onClick={handleGenerate}
          disabled={!rooms.length}
        >
          Generate Word Report
        </button>
      </div>
    </div>
  );
}
